import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MessageService} from '@lib-services/message.service';
import {Work} from '@libs-classes/work.class';
import {IWork, IWorkCreate, IWorkUpdate} from '@lib-typing/work.interface';
import {WorkService} from '@lib-services/work.service';
import {CommonDropdownComponent} from '@lib-ui/common-dropdawn/common-dropdown.component';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {LoaderComponent} from '@lib-ui/loader/loader.component';
import {AuthService} from '@lib-ui/auth/auth.service';
import {ProjectSelectorService} from '@lib-services/project-selector.service';
import {filter, switchMap} from 'rxjs';
import { CloseButtonComponent } from '@lib-ui/close-button/close-button.component';
import {regex} from '@libs-constants/regex';

@Component({
    selector: 'app-work-editor',
    standalone: true,
    imports: [
        CommonModule,
        CommonDropdownComponent,
        MatButton,
        MatDialogContent,
        MatFormField,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        LoaderComponent,
        CloseButtonComponent,
        MatDialogTitle,
    ],
    templateUrl: './work-editor.component.html',
    styleUrl: './work-editor.component.scss'
})
export class WorkEditorComponent implements OnInit {
    work = new Work({} as IWork);
    loading = signal(false);

    readonly dialogRef = inject(MatDialogRef<WorkEditorComponent>);
    readonly fb = inject(FormBuilder);
    readonly workService = inject(WorkService);
    readonly projectSelectorService = inject(ProjectSelectorService);
    protected messagesService = inject(MessageService);
    protected authService = inject(AuthService);
    public data: { workId?: number } = inject(MAT_DIALOG_DATA);
    private regex = regex;

    workForm= this.fb.group({
        name: ['', Validators.required],
        projectId: [0, [Validators.required, Validators.pattern(this.regex.naturalNumber)]],
    });

    ngOnInit(): void {
        if (this.data.workId) {
            this.loading.set(true);
            this.workService.getWork(this.data.workId).subscribe( {
                next: data => {
                    this.work.data = data;
                    this.workForm.setValue(this.work.formData());
                },
                error: error => {this.messagesService.errorMessage(error.message);}
            }).add(() => this.loading.set(false));
        }
    }

    onSubmit(): void {
        if (!this.workForm.valid) {
            return;
        }

        this.loading.set(true);
        const request = this.work.data.id
            ? this.workService.updateWork(this.work.dataForUpdate(this.workForm.value as IWorkUpdate))
            :  this.workService.createWork(this.work.dataForCreate(this.workForm.value as IWorkCreate));

        request.subscribe({
            next: result => {
                this.messagesService.successMessage('Вид работ успешно сохранен');
                this.dialogRef.close(result);},
            error: error => {this.messagesService.errorMessage(error.message);}
        }).add(() => this.loading.set(false));
    }

    deleteWork(id: number): void {
        if(!this.work.data) {return;}

        this.messagesService.confirmMessage({
            title: 'Удаление вида работ',
            text: 'Вы действительно хотите удалить вит работ?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.workService.deleteWorks([id]);
            }),
        ).subscribe({
            next: () => {
                this.messagesService.successMessage('Вид работ успешно удален');
                this.dialogRef.close(true);
            },
            error: () => this.messagesService.errorMessage('При удалении произошла ошибка')
        });
    }
}
